/**
 * Canvas revisions store a flat list of slash-separated paths. The explorer
 * needs a tree, so this module is the one place that conversion happens —
 * kept free of React so it can be unit-tested directly.
 */
import type { CanvasFile } from "../types";

export interface TreeFile {
  kind: "file";
  name: string;
  path: string;
  byteSize: number;
}

export interface TreeFolder {
  kind: "folder";
  name: string;
  path: string;
  children: TreeNode[];
}

export type TreeNode = TreeFile | TreeFolder;

/** Folders sort before files; each group sorts case-insensitively by name. */
function compare(a: TreeNode, b: TreeNode): number {
  if (a.kind !== b.kind) return a.kind === "folder" ? -1 : 1;
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

export function buildTree(files: CanvasFile[]): TreeNode[] {
  const root: TreeNode[] = [];
  const folders = new Map<string, TreeFolder>();

  const folderAt = (path: string): TreeNode[] => {
    if (!path) return root;
    const existing = folders.get(path);
    if (existing) return existing.children;
    const slash = path.lastIndexOf("/");
    const folder: TreeFolder = {
      kind: "folder",
      name: path.slice(slash + 1),
      path,
      children: [],
    };
    folders.set(path, folder);
    folderAt(slash === -1 ? "" : path.slice(0, slash)).push(folder);
    return folder.children;
  };

  for (const file of files) {
    const slash = file.path.lastIndexOf("/");
    folderAt(slash === -1 ? "" : file.path.slice(0, slash)).push({
      kind: "file",
      name: file.path.slice(slash + 1),
      path: file.path,
      byteSize: file.byte_size,
    });
  }

  const sortDeep = (nodes: TreeNode[]) => {
    nodes.sort(compare);
    for (const node of nodes) if (node.kind === "folder") sortDeep(node.children);
  };
  sortDeep(root);
  return root;
}

/** Every folder path in a tree, for expand-all on first load. */
export function folderPaths(nodes: TreeNode[]): string[] {
  return nodes.flatMap(node => (node.kind === "folder" ? [node.path, ...folderPaths(node.children)] : []));
}

const TEXTUAL = /\.(html?|css|jsx?|mjs|tsx?|json|md|svg|txt)$/i;

export function isEditable(path: string): boolean {
  return TEXTUAL.test(path) || !path.split("/").pop()!.includes(".");
}
